import React, { useEffect } from 'react';
import cookieUtil from '~/src/utils/cookies';
import env from '~/src/utils/env';
import analyticsService from '~/src/services/analytics';

type InterceptorProviderProps = {
  children: React.ReactNode;
};

const ignoreList = [
  /authtoken-emulated-user/,
  /\/clio\//, // Ignore /clio/ path segments (Clio Manage proxy endpoints) - their 401s shouldn't logout Draft users
  /\/questionnaires\//, // Ignore questionnaire endpoints - they may 401 when matters are deleted
];

const isIgnorable401 = (url: string) =>
  ignoreList.some((regexp) => regexp.test(url));

export const InterceptorProvider = ({ children }: InterceptorProviderProps) => {
  useEffect(() => {
    const originalFetch = window.fetch;

    // eslint-disable-next-line no-undef
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input.toString();
      const isDraftApiRequest = url.includes(env.apiUrl);

      // Only intercept Draft API requests - pass through all others unchanged
      if (!isDraftApiRequest) {
        return originalFetch(input, init);
      }

      const authHeaders = cookieUtil.getHeaderTokens() || {};
      init = {
        ...init,
        headers: {
          ...init?.headers,
          ...authHeaders,
        },
      };

      const response = await originalFetch(input, init);

      if (response.status === 401 && !isIgnorable401(url)) {
        analyticsService.track('Account Logged Out Automatically');
        cookieUtil.logout();
        window.location.assign('/signin/?auto_logged=true');
      }
      if (
        response.status === 503 &&
        response.statusText === 'Service Unavailable'
      ) {
        window.location.reload();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  return <>{children}</>;
};
